/* eslint-disable react/jsx-max-props-per-line */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Box,
  Button,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { DialogDefaultV2 } from '../../../../components/v2';
import TableService from '../../../../services/TableService';

const UNIDADE_SUFFIX = {
  servicos: 'serviços',
  postes: 'postes',
};

const resolveRowTotal = (row) => {
  if (row.postesDigitados != null) return row.postesDigitados;
  return row.total ?? row.servicosDigitados ?? 0;
};

const MediaDiariaQuickModal = ({
  open,
  onClose,
  apiFilter,
  onSelectDay,
  unidade,
  metaDiaria,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const unitLabel = UNIDADE_SUFFIX[unidade] ?? UNIDADE_SUFFIX.servicos;

  const fetchPorDia = useCallback(async () => {
    setLoading(true);
    try {
      const response = await TableService.request('reports/digitacao/me/por-dia', apiFilter);
      setRows(Array.isArray(response?.data) ? response.data : response?.data?.itens || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiFilter, open]);

  useEffect(() => {
    if (open) fetchPorDia();
  }, [open, fetchPorDia]);

  const title = useMemo(() => {
    if (metaDiaria != null) {
      return `Média por dia · meta ${metaDiaria}/${unitLabel}`;
    }
    return `Média por dia (${unitLabel})`;
  }, [metaDiaria, unitLabel]);

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={onClose}
      title={title}
      confirmMessage="Fechar"
      readOnly
      maxWidth="sm"
      fullWidth
    >
      <DialogContent>
        {loading ? (
          <Typography variant="body2" color="text.secondary">Carregando…</Typography>
        ) : !rows.length ? (
          <Typography variant="body2" color="text.secondary">
            Sem dados diários para o período selecionado.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {rows.map((row) => {
              const dia = row.dia ?? row.data;
              const total = resolveRowTotal(row);
              const label = dia
                ? moment(Number(dia) > 0 ? Number(dia) : dia).format('DD/MM/YYYY (ddd)')
                : '—';

              return (
                <Box
                  key={dia || label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2">{label}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={700}>
                      {total} {unitLabel}
                    </Typography>
                    {onSelectDay && dia && (
                      <Button size="small" onClick={() => onSelectDay(Number(dia) || dia)}>
                        Filtrar
                      </Button>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
    </DialogDefaultV2>
  );
};

MediaDiariaQuickModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  apiFilter: PropTypes.object.isRequired,
  onSelectDay: PropTypes.func,
  unidade: PropTypes.oneOf(['servicos', 'postes']),
  metaDiaria: PropTypes.number,
};

MediaDiariaQuickModal.defaultProps = {
  onSelectDay: undefined,
  unidade: 'servicos',
  metaDiaria: null,
};

export default MediaDiariaQuickModal;
